import { useNavigate, useParams } from "react-router-dom";
import { Button, Modal, Spinner } from "src/components";
import InvoiceLayout from "src/pages/invoice-master/invoice-layout.component";
import PurpleLeftArrowIcon from "src/features/fields/assets/purple-left-arrow.svg";
import { useToast } from "src/hooks/useToast";
import { RootState } from "src/context/store";
import { useDispatch, useSelector } from "react-redux";
import { downloadInvoice, fetchEinvoicesData } from "src/services/invoice-api";
import EInvoiceReject from "./E-InvoiceReject";
import { useEffect, useState } from "react";
import EInvoiceAccept from "./E-InvoiceAccept";
import { invoiceActions } from "src/context/invoice-slice";

const EInvoiceDetails = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedEInvoice } = useSelector((state: RootState) => state.invoice);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [tableData, setTableData] = useState<{ label: string; master: string; invoice: string }[]>([]);
  const { einvoice_dtl_id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const getEInvoicesData = async (page_size: number, page_number: number) => {
    setIsLoading(true);
    if (!einvoice_dtl_id) return;
    try {
      setIsLoading(true);
      const res = await fetchEinvoicesData({ page_size, page_number, einvoice_dtl_id: einvoice_dtl_id || null });
      dispatch(invoiceActions.setSelectedEInvoice(res?.data?.data?.eInvoices?.[0]));
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast({
        variant: "destructive",
        description: "Failed to load e-invoices",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEInvoice) {
      setTableData([
        {
          label: "Invoice No.",
          master: selectedEInvoice?.einvoiceDtls?.[0]?.invoice_number || "",
          invoice: selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.stockist_invoice_number,
        },
        {
          label: "Seller Gst",
          master: selectedEInvoice?.einvoiceDtls?.[0]?.seller_gst || "",
          invoice: selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.stockist_gst_number,
        },
        {
          label: "Buyer Gst",
          master: selectedEInvoice?.einvoiceDtls?.[0]?.buyer_gst || "",
          invoice: selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.counter_gst_number,
        },
        {
          label: "Fuid",
          master: selectedEInvoice?.einvoiceDtls?.[0]?.mapped_fuid_number || "",
          invoice: selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.fuid_no,
        },
        {
          label: "Stockist Id",
          master: selectedEInvoice?.stockist_id || "",
          invoice: selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.stockist_id,
        },
        { label: "IRN", master: selectedEInvoice?.einvoiceDtls?.[0]?.irn_number || "", invoice: "" },
        { label: "QR/IRN", master: selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_ref_type || "", invoice: "" },
        { label: "City", master: selectedEInvoice?.einvoiceDtls?.[0]?.city || "", invoice: "" },
        { label: "MCI No.", master: selectedEInvoice?.einvoiceDtls?.[0]?.mci_no || "", invoice: "" },
        { label: "DL Number*", master: selectedEInvoice?.einvoiceDtls?.[0]?.dl_number || "", invoice: "" },
        { label: "GST No.*", master: selectedEInvoice?.einvoiceDtls?.[0]?.gst_no || "", invoice: "" },
        { label: "GST Status", master: selectedEInvoice?.einvoiceDtls?.[0]?.gst_status || "", invoice: "" },
      ]);
    }
  }, [selectedEInvoice]);

  useEffect(() => {
    getEInvoicesData(10, 1);
  }, [einvoice_dtl_id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <InvoiceLayout>
      <div className="">
        <div className="flex flex-col items-start px-[3.75rem] mt-6 pb-12 shadow-md border-b-[1px] border-b-[#E9E9E9]">
          <Button className="py-2 text-lg text-[#7F56D9]" onClick={() => navigate("/transactions/invoice")}>
            <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
            Back to Invoices
          </Button>
          <h1 className="text-[#49484C] font-bold text-[2rem]">E-Invoices</h1>
        </div>

        <div className="flex px-[3.75rem] mt-10 items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-2">
            <p className="text-2xl font-semibold">{selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.company_name}</p>
            <h1
              className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
              style={{
                backgroundColor:
                  selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_status == "accepted"
                    ? "#D3F6E4"
                    : selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_status == "pending"
                      ? "#FFF9ED"
                      : "#FEE9E8",
                color:
                  selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_status == "accepted"
                    ? "#207A65"
                    : selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_status == "pending"
                      ? "#996B0F"
                      : "#8E1F0B",
              }}
            >
              {selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_status}
            </h1>
          </div>
          <div className="flex items-center p-2 rounded-md bg-[#f1f1f1]">
            <Button
              className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 rounded-r-none border-r rounded-l-md h-5 hover:text-gray-600 focus:outline-none focus:ring-0"
              onClick={() => {
                if (!selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.file_path) {
                  toast({ description: "Invoice not found", variant: "destructive" });
                  return;
                }
                downloadInvoice(selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.file_path).then((res: any) => {
                  if (res?.data?.success && res?.data?.data) {
                    window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                  } else {
                    console.error("Failed to download the invoice.");
                  }
                });
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M7 8l5 5m0 0l5-5m-5 5V3" />
              </svg>
              <span>Download Invoice</span>
            </Button>
            <Button className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1]  border-none rounded-none h-5 hover:text-gray-600 focus:outline-none focus:ring-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              <span>Preview</span>
            </Button>
            <Button className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 border-l rounded-l-none rounded-r-md h-5 hover:text-gray-600 focus:outline-none focus:ring-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 0m12.728 0L12 12m0 0l-6.364-6.364" />
              </svg>
              <span>Report an issue</span>
            </Button>
          </div>
        </div>

        <div className="p-6 mt-10 border border-[#D0D0D0] shadow-sm mx-[3.75rem] mb-20 rounded-lg">
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              border: "1px solid #ccc",
            }}
            className=""
          >
            <thead>
              <tr className="text-sm bg-[#F1F1F1]">
                <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}></th>
                <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>As per Invoice Already Entered</th>
                <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>As per Acknowledged Copy</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} className="w-full">
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/3 text-gray-500">
                    <input type="checkbox" /> <span className="ml-2 text-sm font-normal">{row.label}</span>
                  </td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/3 text-sm">
                    {row.master}
                  </td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/3 text-sm">
                    {row.invoice}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mt-14 gap-x-2">
            <Button
              className="font-normal px-4 py-2 bg-gray-200 border border-gray-300"
              onClick={() => {
                setIsRejectModalOpen(true);
              }}
            >
              Reject
            </Button>
            <Button className="font-normal px-4 py-2 text-white bg-[#8051FF]" onClick={() => setIsAcceptModalOpen(true)}>
              Accept
            </Button>
          </div>
        </div>
        <Modal isOpen={isRejectModalOpen} onCloseModal={() => setIsRejectModalOpen(false)} isHeader={false}>
          <EInvoiceReject
            onSuccess={() => {
              setIsRejectModalOpen(false);
              getEInvoicesData(10, 1);
            }}
            primary_key={selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.stockist_invoice_attachment_id}
            einvoice_dtl_id={selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_dtl_id}
            onCloseModal={() => setIsRejectModalOpen(false)}
          />
        </Modal>
        <Modal isOpen={isAcceptModalOpen} onCloseModal={() => setIsAcceptModalOpen(false)} isHeader={false}>
          <EInvoiceAccept
            onSuccess={() => {
              setIsAcceptModalOpen(false);
              getEInvoicesData(10, 1);
            }}
            primary_key={selectedEInvoice?.einvoiceDtls?.[0]?.stockistInvoiceAttachment?.stockist_invoice_attachment_id}
            einvoice_dtl_id={selectedEInvoice?.einvoiceDtls?.[0]?.einvoice_dtl_id}
            onCloseModal={() => setIsAcceptModalOpen(false)}
          />
        </Modal>
      </div>
    </InvoiceLayout>
  );
};

export default EInvoiceDetails;
